import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../api';
import {
  PageHeader, Loading, ErrorBanner, Empty, Badge, Modal, fmtDate, asList, ui,
} from '../components/adminUi';

// Ustalarning kredit hisobi: to'ldirish so'rovlarini tasdiqlash + balanslarni ko'rish
// va qo'lda tuzatish. Har tuzatish daftarga (credit_transactions) yozuv qo'yadi.
export default function CreditsPage() {
  const { auth } = useAuth();

  const [status, setStatus] = useState('pending');
  const [provider, setProvider] = useState(''); // '' = hammasi | 'payme' | 'manual'
  const [topups, setTopups] = useState([]);
  const [balances, setBalances] = useState([]);
  const [fees, setFees] = useState([]);
  const [feeTotals, setFeeTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [rowMsg, setRowMsg] = useState({});

  // Qo'lda tuzatish modali
  const [adjustFor, setAdjustFor] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [adjustError, setAdjustError] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  // Prime (Faza 5) — qo'lda berish/bekor qilish
  const [primeFor, setPrimeFor] = useState(null);
  const [primeDays, setPrimeDays] = useState('30');
  const [primeError, setPrimeError] = useState('');
  const [primeBusy, setPrimeBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const q = new URLSearchParams({ status });
      if (provider) q.set('provider', provider);
      const [tRes, bRes, fRes] = await Promise.all([
        apiFetch(`/api/admin/credits/topups/list?${q}`, auth.token),
        apiFetch('/api/admin/credits', auth.token),
        apiFetch('/api/admin/credits/deal-fees', auth.token),
      ]);
      if (!tRes.ok || !bRes.ok) throw new Error();
      const t = await tRes.json();
      const b = await bRes.json();
      // Manba filtri mijozda ham qo'llanadi: `provider` ni hali tanimaydigan backend
      // hamma qatorni qaytarsa ham ro'yxat tanlangan manbaga mos bo'lsin.
      const rows = asList(t).map(normalizeTopup);
      setTopups(provider ? rows.filter((r) => r.provider === provider) : rows);
      setBalances(Array.isArray(b) ? b : []);
      // Bitim ulushlari — yiqilsa asosiy sahifa buzilmasin.
      if (fRes.ok) {
        const f = await fRes.json();
        setFees(Array.isArray(f.items) ? f.items : []);
        setFeeTotals(f.totals || null);
      } else {
        setFees([]); setFeeTotals(null);
      }
    } catch {
      setTopups([]);
      setBalances([]);
      setFetchError("Ma'lumotni yuklashda xatolik. Sahifani yangilang.");
    } finally {
      setLoading(false);
    }
  }, [auth.token, status, provider]);

  useEffect(() => { load(); }, [load]);

  const decide = async (row, action) => {
    // Payme so'rovlarini backend 409 PAYME_MANAGED bilan qaytaradi — umuman chaqirmaymiz.
    if (!canModerate(row)) return;
    if (action === 'reject' && !window.confirm(`${row.ustaName} — ${money(row.amount)} so'm so'rovi rad etilsinmi?`)) return;
    setBusyId(row.id);
    setRowMsg((m) => ({ ...m, [row.id]: null }));
    try {
      const res = await apiFetch(`/api/admin/credits/topups/${row.id}/${action}`, auth.token, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setRowMsg((m) => ({
          ...m,
          [row.id]: {
            tone: 'green',
            text: action === 'approve' ? `Tasdiqlandi — yangi balans ${money(d.balance)} so'm` : 'Rad etildi',
          },
        }));
        load();
      } else if (res.status === 409 && d.code === 'PAYME_MANAGED') {
        setRowMsg((m) => ({
          ...m,
          [row.id]: { tone: 'red', text: "Bu to'lov Payme tomonidan boshqariladi, qo'lda tasdiqlab bo'lmaydi" },
        }));
        load();
      } else {
        setRowMsg((m) => ({ ...m, [row.id]: { tone: 'red', text: d.message || 'Xatolik' } }));
      }
    } catch {
      setRowMsg((m) => ({ ...m, [row.id]: { tone: 'red', text: 'Tarmoq xatosi' } }));
    } finally {
      setBusyId(null);
    }
  };

  const openAdjust = (u) => {
    setAdjustFor(u);
    setAdjustAmount('');
    setAdjustNote('');
    setAdjustError('');
  };

  const submitAdjust = async () => {
    const amt = Number(String(adjustAmount).replace(/\s/g, ''));
    if (!Number.isFinite(amt) || amt === 0) {
      setAdjustError("Miqdorni kiriting (qo'shish uchun musbat, yechish uchun manfiy)");
      return;
    }
    setAdjusting(true);
    setAdjustError('');
    try {
      const res = await apiFetch(`/api/admin/credits/${adjustFor.id}/adjust`, auth.token, {
        method: 'POST',
        body: JSON.stringify({ amount: amt, note: adjustNote.trim() || undefined }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setAdjustFor(null);
        load();
      } else {
        setAdjustError(d.message || 'Xatolik');
      }
    } catch {
      setAdjustError('Tarmoq xatosi');
    } finally {
      setAdjusting(false);
    }
  };

  const submitPrime = async (days) => {
    const n = Number(days);
    if (!Number.isFinite(n)) { setPrimeError('Kunlar soni noto\'g\'ri'); return; }
    setPrimeBusy(true);
    setPrimeError('');
    try {
      const res = await apiFetch(`/api/admin/credits/${primeFor.id}/prime`, auth.token, {
        method: 'POST',
        body: JSON.stringify({ days: n }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { setPrimeFor(null); load(); }
      else setPrimeError(d.message || 'Xatolik');
    } catch {
      setPrimeError('Tarmoq xatosi');
    } finally {
      setPrimeBusy(false);
    }
  };

  // Faqat admin hal qila oladigan so'rovlar sanaladi — Payme'nikini Payme o'zi yopadi.
  const pendingCount = topups.filter(canModerate).length;

  return (
    <AdminLayout>
      <PageHeader
        title="Kredit hisoblari"
        subtitle="Ustalarning to'ldirish so'rovlarini tasdiqlang va balanslarini boshqaring"
      >
        <select style={ui.select} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="pending">Kutilayotgan so'rovlar</option>
          <option value="approved">Tasdiqlangan</option>
          <option value="rejected">Rad etilgan</option>
          <option value="all">Hammasi</option>
        </select>
        <select
          style={ui.select}
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          aria-label="Manba"
          title="Manba"
        >
          <option value="">Manba: Hammasi</option>
          <option value="payme">Manba: Payme</option>
          <option value="manual">Manba: Qo'lda</option>
        </select>
        <button style={ui.pagerBtn} onClick={load}>↻ Yangilash</button>
      </PageHeader>

      <ErrorBanner>{fetchError}</ErrorBanner>

      {loading ? (
        <Loading />
      ) : (
        <>
          <h3 style={s.section}>
            To'ldirish so'rovlari
            {pendingCount > 0 && <span style={s.count}>{pendingCount}</span>}
          </h3>

          {topups.length === 0 ? (
            <Empty
              icon="💳"
              title="Hali to'ldirish yo'q"
              hint={emptyHint(status, provider)}
            />
          ) : (
            <div style={ui.tableWrap}>
              <table style={ui.table}>
                <thead>
                  <tr>
                    <th style={ui.th}>Usta</th>
                    <th style={{ ...ui.th, width: '110px' }}>Manba</th>
                    <th style={{ ...ui.th, width: '130px' }}>Summa</th>
                    <th style={{ ...ui.th, width: '140px' }}>Yaratilgan</th>
                    <th style={ui.th}>Holat</th>
                    <th style={{ ...ui.th, width: '215px', textAlign: 'right' }}>Amal</th>
                  </tr>
                </thead>
                <tbody>
                  {topups.map((row) => {
                    const msg = rowMsg[row.id];
                    const busy = busyId === row.id;
                    const isPayme = row.provider === 'payme';
                    return (
                      <tr key={row.id} style={ui.tr}>
                        <td style={ui.td}>
                          <div style={{ fontWeight: '600', color: 'var(--text)' }}>{row.ustaName}</div>
                          <div style={s.sub}>{row.ustaPhone || '—'}</div>
                        </td>
                        <td style={ui.td}>
                          {isPayme ? <Badge tone="blue">💳 Payme</Badge> : <Badge tone="gray">✍️ Qo'lda</Badge>}
                          {!isPayme && row.method && <div style={s.sub}>{row.method}</div>}
                        </td>
                        <td style={{ ...ui.td, fontWeight: '700', color: 'var(--text)' }}>
                          {money(row.amount)} so'm
                        </td>
                        <td style={ui.td}>{fmtDate(row.createdAt, true)}</td>
                        <td style={ui.td}>
                          {isPayme ? <PaymeStatus row={row} /> : <StatusBadge status={row.status} />}
                        </td>
                        <td style={{ ...ui.td, textAlign: 'right' }}>
                          {canModerate(row) ? (
                            <div style={ui.actions}>
                              <button
                                style={{ ...ui.btnSm, ...ui.btnGreen, ...(busy ? ui.btnDisabled : {}) }}
                                onClick={() => decide(row, 'approve')}
                                disabled={busy}
                              >
                                {busy ? '...' : '✅ Tasdiqlash'}
                              </button>
                              <button
                                style={{ ...ui.btnSm, ...ui.btnRed, ...(busy ? ui.btnDisabled : {}) }}
                                onClick={() => decide(row, 'reject')}
                                disabled={busy}
                              >
                                ✕ Rad etish
                              </button>
                            </div>
                          ) : isPayme ? (
                            <Badge tone="blue">Payme orqali — avtomatik</Badge>
                          ) : (
                            <span style={s.decided}>{fmtDate(row.decidedAt, true) || '—'}</span>
                          )}
                          {msg && <div style={{ ...s.msg, ...toneStyle(msg.tone) }}>{msg.text}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {feeTotals && (feeTotals.charged.n > 0 || feeTotals.pending.n > 0 || feeTotals.waived.n > 0) && (
            <>
              <h3 style={{ ...s.section, marginTop: '32px' }}>Bitim ulushlari</h3>
              <div style={s.totals}>
                <Totals label="Olingan" tone="green" v={feeTotals.charged} />
                <Totals label="Kutilmoqda" tone="amber" v={feeTotals.pending} />
                <Totals label="Prime (olinmadi)" tone="gray" v={feeTotals.waived} />
              </div>
              <div style={ui.tableWrap}>
                <table style={ui.table}>
                  <thead>
                    <tr>
                      <th style={{ ...ui.th, width: '90px' }}>E'lon</th>
                      <th style={ui.th}>Usta</th>
                      <th style={ui.th}>Mijoz</th>
                      <th style={{ ...ui.th, width: '130px' }}>Miqdor</th>
                      <th style={{ ...ui.th, width: '110px' }}>To'lovchi</th>
                      <th style={{ ...ui.th, width: '130px' }}>Holat</th>
                      <th style={{ ...ui.th, width: '140px' }}>Sana</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fees.map((f) => (
                      <tr key={f.id} style={ui.tr}>
                        <td style={ui.td}>#{f.taskId}</td>
                        <td style={{ ...ui.td, color: 'var(--text)' }}>
                          {[f.providerFirstName, f.providerLastName].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td style={ui.td}>
                          {[f.clientFirstName, f.clientLastName].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td style={{ ...ui.td, fontWeight: '700', color: 'var(--text)' }}>
                          {money(f.amount)} so'm
                        </td>
                        <td style={ui.td}>{f.payer === 'client' ? 'Mijoz' : 'Usta'}</td>
                        <td style={ui.td}><FeeBadge status={f.status} reason={f.reason} /></td>
                        <td style={ui.td}>{fmtDate(f.createdAt, true)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <h3 style={{ ...s.section, marginTop: '32px' }}>Ustalar balansi</h3>
          <p style={s.hint}>
            Taklif yuborish bepul — ulush mijoz taklifni <b>qabul qilganda</b> (kelishuv paytida)
            olinadi. Balansi ulushga yetmagan usta yangi taklif yubora olmaydi. Qo'lda tuzatish
            daftarga yozuv qo'yadi.
          </p>

          {balances.length === 0 ? (
            <Empty icon="🧰" title="Usta yo'q" hint="Hali ro'yxatdan o'tgan usta yo'q." />
          ) : (
            <div style={ui.tableWrap}>
              <table style={ui.table}>
                <thead>
                  <tr>
                    <th style={ui.th}>Usta</th>
                    <th style={ui.th}>Telefon</th>
                    <th style={{ ...ui.th, width: '170px' }}>Balans</th>
                    <th style={{ ...ui.th, width: '150px' }}>Prime</th>
                    <th style={{ ...ui.th, width: '160px' }}>Oxirgi o'zgarish</th>
                    <th style={{ ...ui.th, width: '210px', textAlign: 'right' }}>Amal</th>
                  </tr>
                </thead>
                <tbody>
                  {balances.map((u) => (
                    <tr key={u.id} style={ui.tr}>
                      <td style={{ ...ui.td, fontWeight: '600', color: 'var(--text)' }}>{fullName(u)}</td>
                      <td style={ui.td}>{u.phone || '—'}</td>
                      <td style={ui.td}>
                        <span style={{ ...s.balance, ...(u.balance <= 0 ? s.balanceLow : {}) }}>
                          {money(u.balance)} so'm
                        </span>
                        {u.balance <= 0 && <Badge tone="red">taklif yubora olmaydi</Badge>}
                      </td>
                      <td style={ui.td}><PrimeCell until={u.primeUntil} /></td>
                      <td style={ui.td}>{fmtDate(u.updatedAt, true) || '—'}</td>
                      <td style={{ ...ui.td, textAlign: 'right' }}>
                        <div style={ui.actions}>
                          <button style={{ ...ui.btnSm, ...ui.btnBlue }} onClick={() => openAdjust(u)}>
                            ± Tuzatish
                          </button>
                          <button
                            style={{ ...ui.btnSm, ...ui.btnGray }}
                            onClick={() => { setPrimeFor(u); setPrimeDays('30'); setPrimeError(''); }}>
                            ⭐ Prime
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {adjustFor && (
        <Modal title={`${fullName(adjustFor)} — balansni tuzatish`} onClose={() => setAdjustFor(null)}>
          <p style={s.modalHint}>
            Joriy balans: <b>{money(adjustFor.balance)} so'm</b>. Qo'shish uchun musbat, yechish
            uchun manfiy miqdor kiriting.
          </p>
          <div style={ui.field}>
            <label style={ui.label}>Miqdor (so'm) <span style={ui.req}>*</span></label>
            <input
              style={ui.input}
              value={adjustAmount}
              onChange={(e) => setAdjustAmount(e.target.value.replace(/[^0-9-]/g, ''))}
              placeholder="masalan: 100000 yoki -50000"
              autoFocus
            />
          </div>
          <div style={ui.field}>
            <label style={ui.label}>Izoh</label>
            <input
              style={ui.input}
              value={adjustNote}
              onChange={(e) => setAdjustNote(e.target.value)}
              placeholder="nima uchun tuzatilyapti"
            />
          </div>
          {adjustError && <div style={ui.formError}>{adjustError}</div>}
          <div style={ui.modalActions}>
            <button style={{ ...ui.btnSm, ...ui.btnGray }} onClick={() => setAdjustFor(null)} disabled={adjusting}>
              Bekor qilish
            </button>
            <button
              style={{ ...ui.saveBtn, ...(adjusting ? ui.btnDisabled : {}) }}
              onClick={submitAdjust}
              disabled={adjusting}
            >
              {adjusting ? '...' : 'Saqlash'}
            </button>
          </div>
        </Modal>
      )}

      {primeFor && (
        <Modal title={`${fullName(primeFor)} — Prime`} onClose={() => setPrimeFor(null)}>
          <p style={s.modalHint}>
            Joriy holat: <b>{primeFor.primeUntil && new Date(primeFor.primeUntil) > new Date()
              ? `faol, ${fmtDate(primeFor.primeUntil)} gacha` : 'faol emas'}</b>.
            Qo'lda berish <b>pul yechmaydi</b> — naqd/tashqi to'lov uchun. Usta o'zi
            balansidan ham sotib olishi mumkin.
          </p>
          <div style={ui.field}>
            <label style={ui.label}>Necha kun qo'shilsin</label>
            <input
              style={ui.input}
              value={primeDays}
              onChange={(e) => setPrimeDays(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="30"
            />
          </div>
          {primeError && <div style={ui.formError}>{primeError}</div>}
          <div style={ui.modalActions}>
            <button
              style={{ ...ui.btnSm, ...ui.btnRed }}
              onClick={() => submitPrime(0)}
              disabled={primeBusy}>
              Bekor qilish (Prime'ni o'chirish)
            </button>
            <button
              style={{ ...ui.saveBtn, ...(primeBusy ? ui.btnDisabled : {}) }}
              onClick={() => submitPrime(primeDays)}
              disabled={primeBusy}>
              {primeBusy ? '...' : 'Qo\'shish'}
            </button>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}

function fullName(r) {
  return [r.firstName, r.lastName].filter(Boolean).join(' ') || `#${r.userId || r.id}`;
}

// Bo'sh joyli format: 100 000 (manfiyda minus oldinda qoladi)
function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  const sign = v < 0 ? '−' : '';
  return sign + Math.round(Math.abs(v)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function StatusBadge({ status }) {
  if (status === 'approved') return <Badge tone="green">Tasdiqlangan</Badge>;
  if (status === 'rejected') return <Badge tone="red">Rad etilgan</Badge>;
  return <Badge tone="amber">Kutilmoqda</Badge>;
}

// ── Payme to'ldirishlari ──────────────────────────────────────────────────────

const PAYME_STATUSES = ['pending', 'paid', 'cancelled', 'refunded'];

// Payme tranzaksiya holati (state) → bizning status.
const PAYME_STATE_STATUS = { 1: 'pending', 2: 'paid', '-1': 'cancelled', '-2': 'refunded' };

// Payme CancelTransaction `reason` kodlari.
const PAYME_CANCEL_REASONS = {
  1: 'Qabul qiluvchi topilmadi',
  2: 'Debet operatsiyasida xato',
  3: "To'lovdan oldin bekor qilindi",
  4: 'Vaqt tugadi (timeout)',
  5: 'Qaytarildi',
  10: "Noma'lum xato",
};

// Backend javobini bir xil ko'rinishga keltiradi. Yangi maydonlar (provider, status,
// rawStatus, canModerate, payme*, usta*) bo'lmasa — eski maydonlardan tiklanadi.
function normalizeTopup(r) {
  const provider = r.provider || (r.method === 'payme' ? 'payme' : 'manual');
  const rawStatus = r.rawStatus || r.status;
  const paymeState = r.paymeState == null || r.paymeState === '' ? null : Number(r.paymeState);
  let status = r.status;
  if (provider === 'payme' && !PAYME_STATUSES.includes(status)) {
    status = PAYME_STATE_STATUS[paymeState]
      || (rawStatus === 'approved' ? 'paid' : rawStatus === 'rejected' ? 'cancelled' : 'pending');
  }
  return {
    ...r,
    provider,
    status,
    rawStatus,
    paymeState,
    ustaName: r.ustaName || fullName(r),
    ustaPhone: r.ustaPhone || r.phone || '',
  };
}

// Tasdiqlash/Rad etish faqat qo'lda kelgan, hali kutilayotgan so'rovlarda.
// Payme so'rovini backend 409 PAYME_MANAGED bilan qaytaradi.
function canModerate(row) {
  if (row.canModerate === false || row.provider === 'payme') return false;
  return (row.rawStatus || row.status) === 'pending';
}

function emptyHint(status, provider) {
  const src = provider === 'payme' ? ' (Payme)' : provider === 'manual' ? " (qo'lda)" : '';
  if (status === 'pending') return `Kutilayotgan to'ldirish so'rovi yo'q${src}.`;
  if (status === 'all') return `To'ldirishlar hali bo'lmagan${src}.`;
  return `Bu holatda to'ldirish topilmadi${src}.`;
}

function PaymeStatusBadge({ status }) {
  if (status === 'paid') return <Badge tone="green">Muvaffaqiyatli</Badge>;
  if (status === 'cancelled') return <Badge tone="red">Bekor qilindi</Badge>;
  if (status === 'refunded') return <Badge tone="purple">Qaytarildi</Badge>;
  return <Badge tone="amber">Kutilmoqda</Badge>;
}

function PaymeStatus({ row }) {
  const reasonCode = row.cancelReason == null || row.cancelReason === '' ? null : Number(row.cancelReason);
  const reason = reasonCode == null ? '' : (PAYME_CANCEL_REASONS[reasonCode] || `sabab ${reasonCode}`);
  const cancelled = row.status === 'cancelled' || row.status === 'refunded';
  return (
    <div>
      <PaymeStatusBadge status={row.status} />
      <div style={s.payme}>
        {row.performTime && <div>To'landi: <b>{fmtPaymeTime(row.performTime)}</b></div>}
        {row.cancelTime && (
          <div>
            Bekor: <b>{fmtPaymeTime(row.cancelTime)}</b>
            {reason && <> — {reason}</>}
          </div>
        )}
        {!row.cancelTime && cancelled && reason && <div>Sabab: {reason}</div>}
        {!row.cancelTime && cancelled && !reason && row.note && <div>{row.note}</div>}
        <div style={s.txId}>
          Payme ID: {row.paymeTransactionId ? <code>{row.paymeTransactionId}</code> : '—'}
        </div>
      </div>
    </div>
  );
}

// ISO satr — umumiy formatlovchi orqali. Raqam (Payme ms epoch) kelsa — Toshkent vaqti.
function fmtPaymeTime(v) {
  if (typeof v === 'number' || /^\d+$/.test(String(v))) {
    const d = new Date(Number(v));
    if (Number.isNaN(d.getTime())) return '—';
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Tashkent', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(d).map((x) => [x.type, x.value]));
    return `${p.day}.${p.month}.${p.year} ${p.hour}:${p.minute}`;
  }
  return fmtDate(v, true);
}

function PrimeCell({ until }) {
  if (!until || new Date(until) <= new Date()) {
    return <span style={{ color: 'var(--text-faint)', fontSize: '13px' }}>—</span>;
  }
  const days = Math.ceil((new Date(until) - Date.now()) / 86400000);
  return <Badge tone="amber">{`⭐ ${days} kun`}</Badge>;
}

function FeeBadge({ status, reason }) {
  if (status === 'charged') return <Badge tone="green">Olindi</Badge>;
  if (status === 'waived') return <Badge tone="gray">{reason === 'prime' ? 'Prime' : 'Olinmadi'}</Badge>;
  return <Badge tone="amber">Kutilmoqda</Badge>;
}

function Totals({ label, tone, v }) {
  return (
    <div style={s.totalCard}>
      <div style={s.totalLabel}>{label}</div>
      <div style={{ ...s.totalValue, ...(tone === 'green' ? { color: 'var(--success)' } : tone === 'amber' ? { color: '#b45309' } : {}) }}>
        {money(v.sum)} so'm
      </div>
      <div style={s.totalCount}>{v.n} ta</div>
    </div>
  );
}

function toneStyle(tone) {
  if (tone === 'green') return { color: 'var(--success)' };
  if (tone === 'red') return { color: 'var(--danger)' };
  return { color: 'var(--text-muted)' };
}

const s = {
  section: {
    margin: '8px 0 12px', fontSize: '16px', fontWeight: '700', color: 'var(--text)',
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  count: {
    background: 'var(--danger)', color: '#fff', borderRadius: '999px',
    padding: '2px 9px', fontSize: '12px', fontWeight: '700',
  },
  hint: { margin: '0 0 14px', fontSize: '13px', color: 'var(--text-muted)', maxWidth: '760px' },
  msg: { fontSize: '12px', fontWeight: '600', marginTop: '6px' },
  decided: { fontSize: '13px', color: 'var(--text-faint)' },
  sub: { fontSize: '12px', color: 'var(--text-faint)', marginTop: '3px' },
  payme: { fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px', lineHeight: 1.5 },
  txId: { color: 'var(--text-faint)', wordBreak: 'break-all' },
  balance: { fontWeight: '700', color: 'var(--text)', marginRight: '8px' },
  balanceLow: { color: 'var(--danger)' },
  modalHint: { margin: '0 0 16px', fontSize: '13px', color: 'var(--text-muted)' },
  totals: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' },
  totalCard: {
    background: 'var(--surface)', border: '1px solid var(--border, #e5e7eb)',
    borderRadius: '10px', padding: '12px 16px', minWidth: '150px',
  },
  totalLabel: { fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' },
  totalValue: { fontSize: '18px', fontWeight: '700', color: 'var(--text)' },
  totalCount: { fontSize: '12px', color: 'var(--text-faint)', marginTop: '2px' },
};
